import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  TextField,
  Button,
  Avatar,
  IconButton,
  InputAdornment,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Drawer,
  Tooltip,
} from "@mui/material";
import RequiredChoiceDialog, { focusRequiredField } from "../RequiredChoiceDialog";
import { formatPhone, formatPhoneDisplay } from "../../utils/formatPhone";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import RedoIcon from "@mui/icons-material/Redo";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";

import LeadStatusCell from "../LeadStatusCell";
import LeadStatusIcon from "../LeadStatusIcon";
import { LEAD_STATUS_DEFINITIONS } from "../../constants/leadStatuses";
import { formatStateAbbr } from "../../constants/usStates";
import ConfirmDialog from "../ConfirmDialog";
import TransferConversationModal from "../modals/TransferConversationModal";
import NewMessageModal from "../modals/NewMessageModal";
import {
  getConversations,
  getConversationMessages,
  sendConversationMessage,
  deleteConversation,
  updateConversation,
} from "../../api/conversations";
import API from "../../utils/api";
import { toast } from "react-toastify";
import { useGlobalCounts } from "../../context/GlobalCountContext";
import { getUserInitials } from "../../utils/userInitials";
import {
  buildAssignedRepOptions,
  formatAssignedRepOptionLabel,
} from "../../utils/assignedRepOptions";

/* ============================================================
   FORMAT HELPERS (UNCHANGED)
============================================================ */
function formatRelativeTime(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);
  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return diffHr === 1 ? "1 hour ago" : `${diffHr} hours ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function formatMessageTime(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return timeStr;
  if (isYesterday) return `Yesterday ${timeStr}`;
  const dateStr = date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
  return `${dateStr} ${timeStr}`;
}

/* ============================================================
   FEATURE FLAG
============================================================ */
const CONVERSATIONS_ENABLED = process.env.REACT_APP_CONVERSATIONS_ENABLED !== 'false';

const STATUS_OPTIONS = LEAD_STATUS_DEFINITIONS.map((d) => ({
  value: d.value,
  icon: d.Icon,
  color: d.color,
  label: d.value,
}));

/* ============================================================
   COMPONENT
============================================================ */
export default function ConversationsPanel({
  role,
  onMobileViewChange,
  canEditLead = true,
  isEditDisabled = false,
}) {
  const { refreshCounts } = useGlobalCounts();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isManager = role === "Admin" || role === "Manager";

  /* ============================================================
     MOCK DATA (fallback only)
  ============================================================ */
  const mockConversations = useMemo(
    () => [
      {
        id: "1",
        lead: { name: "John Smith", phone: "(555) 123-4567", address: "123 Main St, Austin, TX" },
        last_message: "Yes, this afternoon works.",
        messages: [
          { id: "m1", dir: "in", text: "Hi, I saw your ad." },
          { id: "m2", dir: "out", text: "Are you free today?" },
        ],
      },
      {
        id: "2",
        lead: { name: "Sarah Johnson", phone: "(555) 222-8910", address: "88 Pine Rd, Tampa, FL" },
        last_message: "Thanks, that works.",
        messages: [{ id: "m1", dir: "in", text: "Can you send pricing?" }],
      },
    ],
    []
  );

  /* ============================================================
     STATE (UNCHANGED)
  ============================================================ */
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const closeInfoDrawer = () => setInfoDrawerOpen(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [listSort, setListSort] = useState("recent");
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Mobile iOS pattern: list OR thread full-screen
  const [mobileView, setMobileView] = useState("list");

  const [draft, setDraft] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);

  const [notesText, setNotesText] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const searchInputRef = useRef(null);

  const [leadDetails, setLeadDetails] = useState(null);

  const [editingContact, setEditingContact] = useState(false);
  const [followUpDateRequiredOpen, setFollowUpDateRequiredOpen] = useState(false);
  const [contactData, setContactData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    status: "",
    followUpDate: "",
    assigned_rep: "",
    sms_email_opt_in: "",
  });
  const [contactSaveLoading, setContactSaveLoading] = useState(false);
  const leadDetailsScrollRef = useRef(null);


  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  };

  /* ============================================================
     LOAD REAL CONVERSATIONS (UNCHANGED)
  ============================================================ */
  const loadConversations = async () => {
    try {
      setLoading(true);
      const res = await getConversations({ assigned: "me" });
      const list = Array.isArray(res?.data) ? res.data : [];
      setConversations(list);
      refreshCounts();

      if (!isMobile) {
        setSelectedId(list[0]?.id ?? null);
      } else {
        setSelectedId(null);
        setMobileView("list");
      }
    } catch (err) {
      console.error("Failed to load conversations", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (CONVERSATIONS_ENABLED) loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isManager) return;
    const loadUsers = async () => {
      try {
        const res = await API.get("/users/");
        setTeamUsers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load users", err);
      }
    };
    loadUsers();
  }, [isManager]);

  /* ============================================================
     LOAD MESSAGES when conversation selected (UNCHANGED)
  ============================================================ */
  useEffect(() => {
    const id = selectedId;
    if (!CONVERSATIONS_ENABLED || !id) {
      setMessages([]);
      return;
    }
    let cancelled = false;

    const loadMessages = async () => {
      try {
        setMessagesLoading(true);
        const res = await getConversationMessages(id);
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setMessages(list);
        refreshCounts();
      } catch (err) {
        console.error("Failed to load messages", err);
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    };

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [selectedId, refreshCounts]);

  useEffect(() => {
    if (!messagesLoading && (CONVERSATIONS_ENABLED && selectedId ? messages : []).length > 0) {
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesLoading, selectedId, messages]);

  /* ============================================================
     LIFT mobileView to parent (UNCHANGED)
  ============================================================ */
  useEffect(() => {
    if (onMobileViewChange) onMobileViewChange(mobileView);
  }, [mobileView, onMobileViewChange]);

  /* ============================================================
     KEYBOARD SHORTCUTS (UNCHANGED)
  ============================================================ */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        if (e.key === "Escape") {
          e.target.blur();
          if (e.target.getAttribute("placeholder")?.includes("Search")) setSearchQuery("");
          return;
        }
        return;
      }
      if (e.key === "Escape") {
        if (searchQuery.trim()) setSearchQuery("");
        else if (infoDrawerOpen) closeInfoDrawer();
        return;
      }
      if (e.key === "/" || (e.key === "k" && (e.ctrlKey || e.metaKey))) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchQuery, isMobile, infoDrawerOpen]);

  /* ============================================================
     DATA SOURCE (UNCHANGED)
  ============================================================ */
  const conversationData = CONVERSATIONS_ENABLED ? conversations : mockConversations;

  const filteredConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list =
      listFilter === "unread"
        ? conversationData.filter((c) => c.unread_count > 0 || c.has_pending_incoming)
        : conversationData;

    if (q) {
      list = list.filter((c) => {
        const name = (c.lead?.name ?? c.lead_name ?? "").toLowerCase();
        const phone = (c.lead?.phone ?? c.lead_phone ?? "").replace(/\D/g, "");
        const phoneQuery = q.replace(/\D/g, "");
        const lastMsg = (c.last_message ?? "").toLowerCase();
        const address = (c.lead?.address ?? c.lead_address ?? "").toLowerCase();
        return (
          name.includes(q) ||
          lastMsg.includes(q) ||
          address.includes(q) ||
          (phoneQuery.length >= 3 && phone.includes(phoneQuery))
        );
      });
    }

    const sorted = [...list].sort((a, b) => {
      const aClosed = a.status === "Closed";
      const bClosed = b.status === "Closed";
      if (aClosed !== bClosed) return aClosed ? 1 : -1;

      if (listSort === "newest") {
        const aAt = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bAt = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bAt - aAt;
      }
      const aAt = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bAt = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bAt - aAt;
    });

    return sorted;
  }, [conversationData, searchQuery, listFilter, listSort]);

  const selected = conversationData.find((c) => c.id === selectedId) || conversationData[0] || null;

  /* ============================================================
     LOAD LEAD DETAILS (UNCHANGED)
  ============================================================ */
  useEffect(() => {
    const leadId = selected?.lead?.id || selected?.lead_id;
    if (!leadId) {
      setNotesText("");
      setLeadDetails(null);
      setEditingContact(false);
      return;
    }

    setEditingContact(false);
    let cancelled = false;

    const loadLeadDetails = async () => {
      try {
        setNotesLoading(true);
        const res = await API.get(`/leads/${leadId}/`);
        if (!cancelled) {
          setNotesText(res?.data?.notes || "");
          setLeadDetails(res?.data ?? null);
        }
      } catch (err) {
        console.error("Failed to load lead details", err);
        if (!cancelled) setLeadDetails(null);
      } finally {
        if (!cancelled) setNotesLoading(false);
      }
    };

    loadLeadDetails();
    return () => {
      cancelled = true;
    };
  }, [selected?.lead?.id, selected?.lead_id]);

  /* ============================================================
     MOBILE THREAD: lock background scroll, hide app bar, fullscreen
  ============================================================ */
  useEffect(() => {
    const onThread = isMobile && mobileView === "thread";

    if (onThread) {
      document.body.classList.add("chat-thread-open");
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyOverflow = document.body.style.overflow;
      const prevBodyPosition = document.body.style.position;
      const prevBodyWidth = document.body.style.width;

      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";

      return () => {
        document.body.classList.remove("chat-thread-open");
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.position = prevBodyPosition;
        document.body.style.width = prevBodyWidth;
      };
    }

    document.body.classList.remove("chat-thread-open");
    return undefined;
  }, [isMobile, mobileView]);

  /* ============================================================
     NEW MESSAGE (UNCHANGED)
  ============================================================ */
  const handleNewMessageCreated = (conversation, prefillText) => {
    const normalized = {
      ...conversation,
      lead:
        conversation.lead && typeof conversation.lead === "object" && !Array.isArray(conversation.lead)
          ? conversation.lead
          : {
              id: conversation.lead_id ?? conversation.lead,
              name: conversation.lead_name,
              phone: conversation.lead_phone,
              address: conversation.lead_address,
            },
    };
    setConversations((prev) => [normalized, ...prev]);
    setSelectedId(conversation.id);
    setMobileView("thread");
    setDraft(prefillText || "");
  };

  /* ============================================================
     SEND MESSAGE (UNCHANGED)
  ============================================================ */
  const handleSend = async () => {
    if (!selectedId || !draft.trim()) return;
    try {
      setSendLoading(true);
      await sendConversationMessage(selectedId, draft.trim());
      const res = await getConversationMessages(selectedId);
      const list = Array.isArray(res?.data) ? res.data : [];
      setMessages(list);
      setDraft("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send message");
    } finally {
      setSendLoading(false);
    }
  };

  /* ============================================================
     SAVE NOTES (UNCHANGED)
  ============================================================ */
  const handleSaveNotes = async () => {
    const leadId = selected?.lead?.id || selected?.lead_id;
    if (!leadId || notesLoading) return;

    try {
      setNotesLoading(true);
      await API.patch(`/leads/${leadId}/`, { notes: notesText });
      toast.success("Notes saved");
    } catch (err) {
      toast.error("Failed to save notes");
    } finally {
      setNotesLoading(false);
    }
  };

  /* ============================================================
     LEAD DETAILS EDIT / SAVE / CANCEL (UNCHANGED)
  ============================================================ */
  const handleContactEdit = () => {
    const lead = leadDetails || selected?.lead || selected;
    if (!lead) return;
    const incompleteFollowUp = (leadDetails?.followups || []).find((f) => !f.completed);
    const nameParts = lead.first_name || lead.last_name ? [] : (lead.name || "").trim().split(/\s+/);
    const firstName = lead.first_name || nameParts[0] || "";
    const lastName =
      lead.last_name || (nameParts.length > 1 ? nameParts.slice(1).join(" ") : nameParts[1] || "");
    setContactData({
      first_name: firstName,
      last_name: lastName,
      email: lead.email || "",
      phone: formatPhone(lead.phone ?? selected?.lead_phone ?? ""),
      status: lead.status || "",
      followUpDate: incompleteFollowUp?.due_date
        ? new Date(incompleteFollowUp.due_date).toISOString().split("T")[0]
        : "",
      assigned_rep: lead.assigned_rep?.id ?? lead.assigned_rep ?? "",
      sms_email_opt_in: lead.sms_email_opt_in ?? "",
    });
    setEditingContact(true);
  };

  const handleContactCancel = () => {
    setEditingContact(false);
    setContactData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      status: "",
      followUpDate: "",
      assigned_rep: "",
      sms_email_opt_in: "",
    });
  };

  const handleContactSave = async () => {
    const leadId = selected?.lead?.id || selected?.lead_id;
    const lead = leadDetails || selected;
    if (!leadId || !lead) return;

    if (contactData.status === "Follow Up Needed" && !contactData.followUpDate) {
      setFollowUpDateRequiredOpen(true);
      return;
    }

    try {
      setContactSaveLoading(true);
      let assignedRepId = null;
      if (contactData.assigned_rep) {
        if (typeof contactData.assigned_rep === "object" && contactData.assigned_rep?.id != null) {
          assignedRepId = contactData.assigned_rep.id;
        } else if (typeof contactData.assigned_rep === "number") {
          assignedRepId = contactData.assigned_rep;
        } else {
          const parsed = parseInt(contactData.assigned_rep, 10);
          if (!Number.isNaN(parsed)) assignedRepId = parsed;
        }
      }

      const payload = {
        first_name: contactData.first_name?.trim() || null,
        last_name: contactData.last_name?.trim() || null,
        email: contactData.email || null,
        phone: formatPhone(contactData.phone) || null,
        status: contactData.status || null,
        notes: notesText ?? lead.notes ?? "",
        assigned_rep: assignedRepId,
        territory: lead.territory ?? null,
        sms_email_opt_in: contactData.sms_email_opt_in || null,
      };

      const res = await API.patch(`/leads/${leadId}/`, payload);
      setLeadDetails(res.data);

      const followups = res.data?.followups || leadDetails?.followups || [];
      if (contactData.status === "Follow Up Needed" && contactData.followUpDate) {
        try {
          const incompleteFollowUp = followups.find((f) => !f.completed);
          if (incompleteFollowUp) {
            const isInitialSchedule = !incompleteFollowUp.due_date;
            await API.patch(`/followups/${incompleteFollowUp.id}/`, {
              due_date: contactData.followUpDate,
              notes: isInitialSchedule
                ? `Follow-up scheduled for ${contactData.followUpDate}`
                : `Follow-up rescheduled to ${contactData.followUpDate}`,
            });
          } else {
            await API.post("/followups/", {
              lead: leadId,
              due_date: contactData.followUpDate,
              completed: false,
              notes: `Follow-up scheduled for ${contactData.followUpDate}`,
            });
          }
          const refetch = await API.get(`/leads/${leadId}/`);
          setLeadDetails(refetch.data);
        } catch (followUpErr) {
          console.error("Follow-up update failed:", followUpErr);
          toast.warning("Contact updated but follow-up date could not be saved");
        }
      }

      setEditingContact(false);
      await loadConversations();
      toast.success("Contact information updated");
    } catch (err) {
      console.error("Contact save failed:", err);
      toast.error(err.response?.data?.detail || err.response?.data?.error || "Failed to update contact");
    } finally {
      setContactSaveLoading(false);
    }
  };

  /* ============================================================
     RESOLVE CONVERSATION (UNCHANGED)
  ============================================================ */
  const [resolvingConversation, setResolvingConversation] = useState(false);
  const handleResolveConversation = async () => {
    if (!selectedId) return;
    try {
      setResolvingConversation(true);
      await updateConversation(selectedId, { status: "Closed" });
      await loadConversations();
      toast.success("Conversation marked resolved");
    } catch (err) {
      console.error("Resolve conversation failed:", err);
      toast.error("Failed to mark resolved");
    } finally {
      setResolvingConversation(false);
    }
  };

  /* ============================================================
     DELETE CONVERSATION (UNCHANGED)
  ============================================================ */
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleConfirmDelete = async () => {
    if (!selectedId || role !== "Admin") return;
    try {
      setDeletingConversation(true);
      await deleteConversation(selectedId);
      await loadConversations();
      setMessages([]);
      toast.success("Conversation removed");
    } catch (err) {
      console.error("Delete conversation failed:", err);
      toast.error("Failed to remove conversation");
    } finally {
      setDeletingConversation(false);
      setConfirmDelete(false);
    }
  };

  /* ============================================================
     STYLES (NEW, iOS-ish)
  ============================================================ */
  const iosBg = theme.palette.mode === "dark" ? "#000" : "#F2F2F7";
  const iosCard = theme.palette.mode === "dark" ? "#111" : "#fff";
  const iosBorder = theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)";

  const bubbleSx = (dir) => ({
    width: "fit-content",
    maxWidth: "75%",
    px: 1.5,
    py: 1,
    borderRadius: "14px",
    fontSize: 15,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    ...(dir === "out"
      ? {
          ml: "auto",
          background: "#2563eb",
          color: "#fff",
        }
      : {
          mr: "auto",
          background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "#f1f5f9",
          color: theme.palette.mode === "dark" ? "#fff" : "#000",
        }),
  });

  const actionIconBase = {
    width: 36,
    height: 36,
    borderRadius: "10px",
    border: "1px solid rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all .15s ease",
  };

  const actionStyles = {
    transfer: {
      ...actionIconBase,
      background: "rgba(59,130,246,0.12)",
      color: "#2563eb",
    },
    resolved: {
      ...actionIconBase,
      background: "rgba(34,197,94,0.12)",
      color: "#16a34a",
    },
    info: {
      ...actionIconBase,
      background: "rgba(245,158,11,0.14)",
      color: "#d97706",
    },
    trash: {
      ...actionIconBase,
      background: "rgba(239,68,68,0.12)",
      color: "#dc2626",
    },
  };

  const transferIconSx = { ...actionStyles.transfer, width: 44, height: 44, borderRadius: "12px" };
  const resolvedIconSx = { ...actionStyles.resolved, width: 44, height: 44, borderRadius: "12px" };
  const infoIconSx    = { ...actionStyles.info,     width: 44, height: 44, borderRadius: "12px" };

  if (!CONVERSATIONS_ENABLED) {
    return (
      <Box sx={{ maxWidth: 1400, mx: "auto", px: 2 }}>
        <Paper
          sx={{
            mt: 2,
            p: 4,
            height: "70vh",
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Box maxWidth={420}>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              Conversations
            </Typography>
            <Typography color="text.secondary">
              SMS conversations are a subscription add-on feature. 
These features help businesses stay connected with customers, respond quickly to inquiries, and manage customer interactions more efficiently.
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  /* ============================================================
     LEAD DETAILS PANEL (same content as before, just extracted)
============================================================ */
   const panelBg =
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(19,24,34,0.98) 0%, rgba(12,16,24,0.98) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,249,252,0.98) 100%)";

  const softBorder =
    theme.palette.mode === "dark"
      ? "1px solid rgba(255,255,255,0.10)"
      : "1px solid rgba(15,23,42,0.08)";

  const glassCardSx = {
    borderRadius: "18px",
    border: softBorder,
    background:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(255,255,255,0.86)",
    backdropFilter: "blur(12px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 10px 28px rgba(0,0,0,0.22)"
        : "0 10px 28px rgba(15,23,42,0.08)",
  };

  const sectionLabelSx = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "text.secondary",
    mb: 0.75,
  };

  const detailRowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 0.25,
  };

  const compactFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(248,250,252,0.95)",
    },
  };

  const LeadDetailsPanel = (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        background: panelBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          pt: 1.75,
          pb: 1.25,
          borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
          backdropFilter: "blur(14px)",
          background:
            theme.palette.mode === "dark"
              ? "rgba(10,14,22,0.72)"
              : "rgba(255,255,255,0.72)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, fontSize: 17, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
              Lead Details
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 12.5, color: "text.secondary" }}>
              View and update lead information
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            {editingContact ? (
              <>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleContactSave}
                  disabled={contactSaveLoading}
                  sx={{
                    minWidth: 0,
                    px: 1.5,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 800,
                    boxShadow: "0 8px 18px rgba(37,99,235,0.22)",
                  }}
                >
                  {contactSaveLoading ? <CircularProgress size={18} color="inherit" /> : "Save"}
                </Button>
                <Button
                  size="small"
                  onClick={handleContactCancel}
                  disabled={contactSaveLoading}
                  sx={{
                    minWidth: 0,
                    px: 1.4,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              canEditLead &&
              !isEditDisabled && (
                <IconButton
                  size="small"
                  onClick={handleContactEdit}
                  title="Edit contact"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                    border: softBorder,
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(59,130,246,0.14)"
                        : "rgba(59,130,246,0.10)",
                    color: "#2563eb",
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )
            )}

            <IconButton
              size="small"
              onClick={closeInfoDrawer}
              title="Close"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "12px",
                border: softBorder,
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.04)"
                    : "rgba(15,23,42,0.04)",
              }}
            >
              ✕
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {selected ? (
        <>
          <Box
            ref={leadDetailsScrollRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: 2,
              py: 1.75,
            }}
          >
            {/* Identity / Overview card */}
            <Box sx={{ ...glassCardSx, p: 1.6, mb: 1.5 }}>
              <Stack spacing={1.1}>
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                    {leadDetails
                      ? [leadDetails.first_name, leadDetails.last_name].filter(Boolean).join(" ") || "—"
                      : selected.lead?.name || "—"}
                  </Typography>

                  <Box sx={{ mt: 0.85 }}>
                    <LeadStatusCell status={leadDetails?.status ?? selected?.status ?? "—"} />
                  </Box>
                </Box>

                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <LocationOnIcon sx={{ color: "error.main", fontSize: 18, mt: 0.2 }} />
                  <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                    {leadDetails
                      ? [leadDetails.address, leadDetails.city, formatStateAbbr(leadDetails.state), leadDetails.zip_code]
                          .filter(Boolean)
                          .join(", ") || "—"
                      : selected.lead?.address || "—"}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                    gap: 1,
                    pt: 0.3,
                  }}
                >
                  <Box
                    sx={{
                      p: 1.1,
                      borderRadius: "14px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.035)"
                          : "rgba(248,250,252,0.95)",
                      border: softBorder,
                    }}
                  >
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Assigned Rep
                    </Typography>
                    <Typography sx={{ mt: 0.4, fontSize: 13.5, fontWeight: 700 }}>
                      {leadDetails?.assigned_rep_name || "Unassigned"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.1,
                      borderRadius: "14px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.035)"
                          : "rgba(248,250,252,0.95)",
                      border: softBorder,
                    }}
                  >
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Territory
                    </Typography>
                    <Typography sx={{ mt: 0.4, fontSize: 13.5, fontWeight: 700 }}>
                      {leadDetails?.territory_name || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {!editingContact ? (
              <>
                {/* Contact info */}
                <Box sx={{ ...glassCardSx, p: 1.5, mb: 1.5 }}>
                  <Typography sx={sectionLabelSx}>Contact Info</Typography>

                  <Stack spacing={1.1}>
                    <Box sx={detailRowSx}>
                      <EmailIcon sx={{ color: "primary.main", fontSize: 18, mt: 0.15 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700 }}>
                          Email
                        </Typography>
                        <Typography variant="body2">
                          {leadDetails?.email || selected.lead?.email || "—"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={detailRowSx}>
                      <PhoneIcon sx={{ color: "success.main", fontSize: 18, mt: 0.15 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700 }}>
                          Phone
                        </Typography>
                        <Typography variant="body2">
                          {formatPhoneDisplay(leadDetails?.phone ?? selected.lead?.phone ?? selected.lead_phone) || "—"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={detailRowSx}>
                      <PersonIcon sx={{ color: "text.secondary", fontSize: 18, mt: 0.15 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700 }}>
                          Full Name
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {leadDetails
                            ? [leadDetails.first_name, leadDetails.last_name].filter(Boolean).join(" ") || "—"
                            : selected.lead?.name || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </>
            ) : (
              <>
                {/* Edit section */}
                <Box sx={{ ...glassCardSx, p: 1.5, mb: 1.5 }}>
                  <Typography sx={sectionLabelSx}>Edit Lead</Typography>

                  <Stack spacing={1.2}>
                    <TextField
                      label="First name"
                      size="small"
                      fullWidth
                      value={contactData.first_name}
                      onChange={(e) => setContactData((c) => ({ ...c, first_name: e.target.value }))}
                      sx={compactFieldSx}
                    />
                    <TextField
                      label="Last name"
                      size="small"
                      fullWidth
                      value={contactData.last_name}
                      onChange={(e) => setContactData((c) => ({ ...c, last_name: e.target.value }))}
                      sx={compactFieldSx}
                    />
                    <TextField
                      label="Email"
                      size="small"
                      fullWidth
                      type="email"
                      value={contactData.email}
                      onChange={(e) => setContactData((c) => ({ ...c, email: e.target.value }))}
                      sx={compactFieldSx}
                    />
                    <TextField
                      label="Phone"
                      size="small"
                      fullWidth
                      value={formatPhone(contactData.phone)}
                      placeholder="(xxx) xxx-xxxx"
                      onChange={(e) => setContactData((c) => ({ ...c, phone: formatPhone(e.target.value) }))}
                      sx={compactFieldSx}
                    />

                    <FormControl size="small" fullWidth sx={compactFieldSx} disabled={!contactData.phone}>
                      <InputLabel>SMS/Email Opt-In</InputLabel>
                      <Select
                        value={contactData.sms_email_opt_in ?? ""}
                        label="SMS/Email Opt-In"
                        onChange={(e) => setContactData((c) => ({ ...c, sms_email_opt_in: e.target.value }))}
                      >
                        <MenuItem value=""><em>—</em></MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                      {!contactData.phone && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Enter a phone number to set opt-in status
                        </Typography>
                      )}
                    </FormControl>

                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: "14px",
                        background:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(248,250,252,0.8)",
                        border: softBorder,
                      }}
                    >
                      <Typography sx={sectionLabelSx}>Address</Typography>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <LocationOnIcon sx={{ color: "error.main", mt: 0.15, fontSize: 18 }} />
                        <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                          {leadDetails
                            ? [leadDetails.address, leadDetails.city, formatStateAbbr(leadDetails.state), leadDetails.zip_code]
                                .filter(Boolean)
                                .join(", ") || "—"
                            : selected.lead?.address || "—"}
                        </Typography>
                      </Stack>
                    </Box>

                    <FormControl size="small" fullWidth sx={compactFieldSx}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={contactData.status}
                        label="Status"
                        onChange={(e) => setContactData((c) => ({ ...c, status: e.target.value }))}
                        MenuProps={{ sx: { zIndex: 2400, borderRadius: "16px" } }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LeadStatusIcon status={opt.value} size={18} />
                              <span>{opt.label}</span>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {contactData.status === "Follow Up Needed" && (
                      <TextField
                        id="message-center-follow-up-date"
                        label="Follow-up date"
                        size="small"
                        fullWidth
                        type="date"
                        value={contactData.followUpDate}
                        onChange={(e) => setContactData((c) => ({ ...c, followUpDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        sx={compactFieldSx}
                      />
                    )}

                    <FormControl size="small" fullWidth disabled={role === "Sales Rep"} sx={compactFieldSx}>
                      <InputLabel>Assigned Rep</InputLabel>
                      <Select
                        value={contactData.assigned_rep ?? ""}
                        label="Assigned Rep"
                        onChange={(e) => setContactData((c) => ({ ...c, assigned_rep: e.target.value }))}
                        MenuProps={{ sx: { zIndex: 2400, borderRadius: "16px" } }}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {buildAssignedRepOptions({
                          users: teamUsers,
                          currentRepId: contactData.assigned_rep,
                          currentRepName: leadDetails?.assigned_rep_name,
                          requireAssignableRole: false,
                        }).map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {formatAssignedRepOptionLabel(u)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box
                      sx={{
                        p: 1.1,
                        borderRadius: "14px",
                        background:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(248,250,252,0.8)",
                        border: softBorder,
                      }}
                    >
                      <Typography sx={sectionLabelSx}>Territory</Typography>
                      <Typography variant="body2">{leadDetails?.territory_name || "—"}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </>
            )}

            {/* Notes */}
            <Box sx={{ ...glassCardSx, p: 1.5, mb: 1 }}>
              <Typography sx={sectionLabelSx}>Notes</Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Add notes…"
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                onBlur={handleSaveNotes}
                disabled={notesLoading}
                size="small"
                sx={{
                  ...compactFieldSx,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    alignItems: "flex-start",
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(248,250,252,0.95)",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Footer actions */}
          <Box
            sx={{
              flexShrink: 0,
              p: 2,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
              background:
                theme.palette.mode === "dark"
                  ? "rgba(10,14,22,0.74)"
                  : "rgba(255,255,255,0.8)",
              backdropFilter: "blur(14px)",
            }}
          >
            <Button
              fullWidth
              size="medium"
              startIcon={<PhoneInTalkIcon />}
              sx={{
                borderRadius: "14px",
                minHeight: 42,
                textTransform: "none",
                fontWeight: 800,
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(15,23,42,0.04)",
                border: softBorder,
              }}
            >
              Call Lead
            </Button>

            {isManager && (
              <Button
                fullWidth
                size="medium"
                variant="contained"
                sx={{
                  mt: 1,
                  borderRadius: "14px",
                  minHeight: 44,
                  textTransform: "none",
                  fontWeight: 800,
                  boxShadow: "0 10px 22px rgba(37,99,235,0.22)",
                }}
                startIcon={<RedoIcon />}
                onClick={() => setTransferOpen(true)}
              >
                Transfer Conversation
              </Button>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ p: 2.5 }}>
          <Typography color="text.secondary">Lead details appear here</Typography>
        </Box>
      )}
    </Box>
  );

  /* ============================================================
     iOS LIST (Mobile Full Screen)
============================================================ */
  const MobileList = (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: iosBg,
      }}
    >
      {/* iOS-ish header */}
      <Box
        sx={{
          px: 2,
          py: 1.2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          bgcolor: iosCard,
          borderBottom: `1px solid ${iosBorder}`,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 20,
              lineHeight: 1.1,
              letterSpacing: "-0.2px",
            }}
          >
            Messages
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              color: "text.secondary",
              mt: 0.25,
            }}
          >
            Customer conversations
          </Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setNewMessageOpen(true)}
          sx={{
            borderRadius: "14px",
            px: 1.4,
            py: 0.75,
            minHeight: 36,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "0 6px 16px rgba(37,99,235,0.22)",
          }}
        >
          New Conversation
        </Button>
      </Box>

      <Box sx={{ px: 2, py: 1.25, bgcolor: iosCard, borderBottom: `1px solid ${iosBorder}` }}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 999,
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#EFEFF4",
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", bgcolor: iosCard }}>
        {loading && (
          <Typography sx={{ p: 2 }} color="text.secondary">
            Loading…
          </Typography>
        )}

        {!loading && conversationData.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No conversations yet
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewMessageOpen(true)}>
              New Message
            </Button>
          </Box>
        )}

        {!loading &&
          filteredConversations.map((c) => (
            <Box
              key={c.id}
              onClick={() => {
                setSelectedId(c.id);
                setMobileView("thread");
              }}
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderBottom: `1px solid ${iosBorder}`,
                cursor: "pointer",
                "&:active": {
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Avatar sx={{ width: 44, height: 44, fontSize: 16, bgcolor: "primary.main", color: "#fff", flexShrink: 0 }}>
                {getUserInitials(
                  {
                    first_name: c.lead?.first_name,
                    last_name: c.lead?.last_name,
                    full_name: c.lead?.name,
                  },
                  "U"
                )}
              </Avatar>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.25 }}>
                  <Typography fontWeight={700} noWrap sx={{ fontSize: 15 }}>
                    {c.lead?.name || "Unknown"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12, flexShrink: 0 }}>
                    {c.last_message_at ? formatRelativeTime(c.last_message_at) : ""}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 14, lineHeight: 1.35 }}>
                  {c.last_message || "No messages yet"}
                </Typography>
              </Box>

              {(c.unread_count > 0 || c.has_pending_incoming) && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    borderRadius: 10,
                    bgcolor: "primary.main",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {c.unread_count > 0 ? c.unread_count : "1"}
                </Box>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );

  /* ============================================================
     iOS THREAD (Mobile Full Screen) - rendered inside fullscreen overlay
============================================================ */
  const MobileThread = (
    <Box
      sx={{
        height: "95%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: iosBg,
      }}
    >
      {/* iOS-like top bar */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2200,
          px: 1,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: iosCard,
          borderBottom: `1px solid ${iosBorder}`,
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={() => {
            setMobileView("list");
            closeInfoDrawer();
          }}
          sx={{ width: 44, height: 44 }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={800} noWrap sx={{ fontSize: 16 }}>
            {selected?.lead?.name || "Unknown"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {formatPhoneDisplay(selected?.lead?.phone || selected?.lead_phone) || "No phone"}
          </Typography>
        </Box>

        {isManager && (
          <IconButton
            onClick={() => setTransferOpen(true)}
            sx={transferIconSx}
            title="Transfer"
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        )}

        <IconButton
          onClick={handleResolveConversation}
          disabled={resolvingConversation || selected?.status === "Closed"}
          sx={resolvedIconSx}
          title="Mark resolved"
        >
          <TaskAltIcon fontSize="small" />
        </IconButton>

        <IconButton
          onClick={() => setInfoDrawerOpen(true)}
          sx={infoIconSx}
          title="Lead details"
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages (ONLY scroller) */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 1.25,
          pt: 1.25,
          pb: "160px", // space for fixed composer + safe-area
          WebkitOverflowScrolling: "touch",
        }}
      >
        {messagesLoading ? (
          <Typography sx={{ py: 2 }} color="text.secondary">
            Loading messages…
          </Typography>
        ) : (
          <>
            {(CONVERSATIONS_ENABLED && selectedId ? messages : selected?.messages || []).map((m) => {
              const direction = m.direction ?? m.dir;
              const body = m.body ?? m.text;
              const timestamp = formatMessageTime(m.created_at);
              return (
                <Box
                  key={m.id}
                  sx={{
                    mb: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: direction === "out" ? "flex-end" : "flex-start",
                  }}
                >
                  <Box sx={bubbleSx(direction)}>{body}</Box>
                  {timestamp && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, mt: 0.35 }}>
                      {timestamp}
                    </Typography>
                  )}
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Composer (FIXED) */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2100,
          px: 1.25,
          py: 1,
          bgcolor: iosCard,
          borderTop: `1px solid ${iosBorder}`,
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          pb: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        }}
      >
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          minRows={1}
          placeholder="Message"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 999,
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#EFEFF4",
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!draft.trim() || sendLoading}
          sx={{
            width: 44,
            height: 44,
            bgcolor:
              draft.trim() && !sendLoading
                ? theme.palette.mode === "dark"
                  ? "#0A84FF"
                  : "#007AFF"
                : "transparent",
            color: draft.trim() && !sendLoading ? "#fff" : "text.secondary",
            "&:hover": {
              bgcolor:
                draft.trim() && !sendLoading
                  ? theme.palette.mode === "dark"
                    ? "#0A84FF"
                    : "#007AFF"
                  : "transparent",
            },
          }}
          title="Send"
        >
          {sendLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Box>

    </Box>
  );

  /* ============================================================
     DESKTOP (keep your 3-column layout)
============================================================ */
  const DesktopLayout = (
    <Paper
      sx={{
        flex: 1,
        minHeight: 640,
        maxHeight: "calc(100dvh - 160px)",
        height: "calc(100dvh - 160px)",
        overflow: "hidden",
        borderRadius: "24px",
        border: "1px solid",
        borderColor: iosBorder,
        bgcolor: iosCard,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 12px 32px rgba(0,0,0,0.28)"
            : "0 12px 32px rgba(0,0,0,0.08)",
        display: "grid",
        gridTemplateColumns: "minmax(280px, 340px) 1px minmax(480px, 1fr)",
      }}
    >
      {/* LEFT */}
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 18,
                  lineHeight: 1.1,
                  letterSpacing: "-0.2px",
                }}
              >
                Conversations
              </Typography>
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: "text.secondary",
                  mt: 0.35,
                }}
              >
                Active customer threads
              </Typography>
            </Box>

            <Button
              data-tour="conversations-new"
              size="small"
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setNewMessageOpen(true)}
              sx={{
                borderRadius: "14px",
                px: 1.5,
                py: 0.8,
                minHeight: 36,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "0 6px 16px rgba(37,99,235,0.22)",
              }}
            >
              New Conversation
            </Button>
          </Box>
          <Divider sx={{ my: 1, flexShrink: 0 }} />
          <Box data-tour="conversations-search">
          <TextField
            inputRef={searchInputRef}
            size="small"
            placeholder="Search by name, phone…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              },
            }}
          />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }} data-tour="conversations-list">
          {loading && (
            <Typography sx={{ p: 2 }} color="text.secondary">
              Loading…
            </Typography>
          )}

          {!loading && filteredConversations.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {conversationData.length === 0 ? "No conversations yet" : "No matches"}
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewMessageOpen(true)}>
                New Message
              </Button>
            </Box>
          )}

          {!loading &&
            filteredConversations.map((c) => (
              <Box
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: "12px",
                  cursor: "pointer",
                  transition: "all .15s ease",
                  ...(c.id === selected?.id
                    ? {
                        background: "rgba(59,130,246,0.12)",
                        border: "1px solid rgba(59,130,246,0.25)",
                      }
                    : {
                        "&:hover": {
                          background: "rgba(59,130,246,0.06)",
                        },
                      }),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Avatar sx={{ width: 36, height: 36, flexShrink: 0, fontSize: 14, bgcolor: "primary.main", color: "#fff" }}>
                    {getUserInitials(
                      {
                        first_name: c.lead?.first_name,
                        last_name: c.lead?.last_name,
                        full_name: c.lead?.name,
                      },
                      "U"
                    )}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                      <Typography fontWeight={700} fontSize={13} noWrap>
                        {c.lead?.name || "Unknown"}
                      </Typography>
                      {c.last_message_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, flexShrink: 0 }}>
                          {formatRelativeTime(c.last_message_at)}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" noWrap color="text.secondary">
                      {c.last_message || "No messages yet"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem />

      {/* CENTER */}
      <Box
        data-tour="conversations-thread"
        sx={{ minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {selected ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={800} fontSize={15} noWrap>
                  {selected.lead?.name || "Unknown"}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                  {formatPhoneDisplay(selected.lead?.phone || selected.lead_phone) || "No phone"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isManager && (
                  <Tooltip title="Transfer">
                    <Box sx={actionStyles.transfer} onClick={() => setTransferOpen(true)}>
                      <RedoIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                )}

                <Tooltip title="Mark resolved">
                  <Box
                    sx={{
                      ...actionStyles.resolved,
                      opacity: resolvingConversation || selected?.status === "Closed" ? 0.4 : 1,
                      pointerEvents: resolvingConversation || selected?.status === "Closed" ? "none" : "auto",
                    }}
                    onClick={resolvingConversation || selected?.status === "Closed" ? undefined : handleResolveConversation}
                  >
                    <TaskAltIcon fontSize="small" />
                  </Box>
                </Tooltip>

                <Tooltip title="Lead details">
                  <Box sx={actionStyles.info} onClick={() => setInfoDrawerOpen(true)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </Box>
                </Tooltip>

                {role === "Admin" && (
                  <Tooltip title="Remove conversation">
                    <Box
                      sx={{
                        ...actionStyles.trash,
                        opacity: deletingConversation ? 0.4 : 1,
                        pointerEvents: deletingConversation ? "none" : "auto",
                      }}
                      onClick={deletingConversation ? undefined : () => setConfirmDelete(true)}
                    >
                      <DeleteIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, p: 1.5, overflowY: "auto" }}>
              {messagesLoading ? (
                <Typography sx={{ py: 2 }} color="text.secondary">
                  Loading messages…
                </Typography>
              ) : (
                <>
                  {(CONVERSATIONS_ENABLED && selectedId ? messages : selected.messages || []).map((m) => {
                    const direction = m.direction ?? m.dir;
                    const body = m.body ?? m.text;
                    const timestamp = formatMessageTime(m.created_at);
                    return (
                      <Box
                        key={m.id}
                        sx={{
                          mb: 1.25,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: direction === "out" ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box sx={bubbleSx(direction)}>{body}</Box>
                        {timestamp && (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, mt: 0.35 }}>
                            {timestamp}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                p: 2,
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
                display: "flex",
                alignItems: "flex-end",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={3}
                minRows={1}
                placeholder="Type a message…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                }}
              />
              <Button
                variant="contained"
                startIcon={sendLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSend}
                disabled={!draft.trim() || sendLoading}
              >
                {sendLoading ? "Sending…" : "Send"}
              </Button>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom color="text.secondary">
              Select a conversation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a conversation from the list to start messaging.
            </Typography>
          </Box>
        )}
      </Box>

    </Paper>
  );

  /* ============================================================
     RENDER ROOT
============================================================ */
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: isMobile ? "none" : 1400,
        mx: isMobile ? 0 : "auto",
        px: isMobile ? 0 : 2,
        bgcolor: isMobile ? iosBg : "transparent",
        position: "relative",
      }}
    >
      {/* Mobile: list normally; thread as FULLSCREEN overlay */}
      {isMobile ? (
        mobileView === "thread" ? (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 2000,
              bgcolor: iosBg,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {MobileThread}
          </Box>
        ) : (
          MobileList
        )
      ) : (
        DesktopLayout
      )}

      {/* Modals + dialogs (UNCHANGED) */}
      <TransferConversationModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        conversationId={selected?.id}
        users={teamUsers}
        onTransferred={loadConversations}
      />

      <NewMessageModal
        open={newMessageOpen}
        onClose={() => setNewMessageOpen(false)}
        onCreated={handleNewMessageCreated}
      />

      <RequiredChoiceDialog
        open={followUpDateRequiredOpen}
        onClose={() => setFollowUpDateRequiredOpen(false)}
        eyebrow="Save contact"
        title="Follow-up date required"
        message="Choose a follow-up date before saving this contact."
        actionLabel="Choose date"
        zIndex={8000}
        onAction={() => {
          setFollowUpDateRequiredOpen(false);
          focusRequiredField("message-center-follow-up-date");
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Conversation"
        message="Remove this conversation? This cannot be undone."
        itemName={selected?.lead?.name || selected?.lead_name || null}
        confirmText="Remove"
        cancelText="Cancel"
        severity="error"
      />

      {/* Lead details: right slideout drawer (Flux-style) — z-index above mobile overlay (2000) */}
      <Drawer
        anchor="right"
        open={infoDrawerOpen}
        onClose={closeInfoDrawer}
        slotProps={{ root: { sx: { zIndex: 2300 } } }}
        ModalProps={{ sx: { zIndex: 2300 } }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 360 },
            maxWidth: "100%",
          },
        }}
      >
        {LeadDetailsPanel}
      </Drawer>
    </Box>
  );
}